<template>
  <div :class="containerClass">
    <div class="solutionItem flex flex-col items-center justify-center">
      <img
        :src="solutionImg"
        alt="solution"
        :class="solutionImgClass"
        class=" max-w-[100px] sm:max-w-[122px] md:max-w-[144px] "
      >
      <div :class="descriptionClass">
        以 XR 技术为核心，构建全场景 LBE 商业解决方案矩阵，精准赋能多元行业场景
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-3 justify-center gap-[20px] lg:gap-[81px] pt-8 sm:pt-12 md:pt-16 lg:pt-[56px] scroll-fade-in
      pb-8 sm:pb-12 md:pb-16 lg:pb-[52px]
      ">
        <div
          v-for="(item, index) in solutionItems"
          :key="index"
          :class="cardClass"
          @click="handleCardClick(index)"
          class="cursor-pointer hover:opacity-90 hover:scale-105 transition-all duration-300 ease-in-out"
        >
          <img
            :src="item.image"
            :alt="item.alt"
            class="w-[118px] h-[118px] mb-[44px]"
          >
          <div :class="titleClass">
            {{ item.title }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import solutionImgSrc from "@/assets/img/solution.webp";
// 如果有白色文字的解决方案图片，请取消下面的注释并导入
import solutionWhiteImgSrc from "@/assets/img/solution-white.webp";
import xrthemImg from "@/assets/img/xrthem.webp";
import xrbigImg from "@/assets/img/xrbig.webp";
import lbeImg from "@/assets/img/lbe.webp";

const router = useRouter();

// 定义 props
interface Props {
  variant?: "default" | "blue"; // default: 首页样式, blue: 解决方案页面样式
}

const props = withDefaults(defineProps<Props>(), {
  variant: "default",
});

// 解决方案数据
const solutionItems = [
  {
    title: "XR剧院内容及管理方案",
    image: xrthemImg,
    alt: "xrthem",
  },
  {
    title: "XR大空间地产家装方案",
    image: xrbigImg,
    alt: "xrbig",
  },
  {
    title: "LBE大空间定制开发方案",
    image: lbeImg,
    alt: "lbe",
  },
];

// 计算样式类
const containerClass = computed(() => {
  return props.variant === "blue" ? "solution bg-[#0C3D8E]" : "solution";
});

const descriptionClass = computed(() => {
  const baseClass =
    "title-family  sm:text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px] text-center scroll-fade-in";
  return props.variant === "blue"
    ? `${baseClass} text-white`
    : `${baseClass} text-[#333333]`;
});

const cardClass = computed(() => {
  const baseClass =
    "flex flex-col items-center justify-center border-[1px] border-[#BEBEBE] rounded-[32px] w-[332px] h-[332px] scroll-fade-in";
  return props.variant === "blue"
    ? `${baseClass} bg-white`
    : `${baseClass} bg-[#F0F2F5]`;
});

const titleClass = computed(() => {
  const baseClass =
    "sm:text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px] text-center font-bold";
  // 卡片标题文字始终使用深色，因为卡片背景是白色或浅色
  return `${baseClass} text-[#07409D]`;
});

// 根据 variant 选择解决方案图片
const solutionImg = computed(() => {
  // 如果是蓝色变体且有白色文字图片，使用白色文字图片
  if (props.variant === "blue" && solutionWhiteImgSrc) {
    return solutionWhiteImgSrc;
  }
  // 否则使用默认图片
  return solutionImgSrc;
});

// 解决方案图片的样式类
const solutionImgClass = computed(() => {
  const baseClass = "pb-[46px] pt-[68px] scroll-fade-in";
  // 如果没有白色文字图片，在蓝色背景下使用 CSS filter 尝试反转颜色
  // 注意：CSS filter 效果可能不理想，建议提供专门的白色文字图片
  if (props.variant === "blue" && !solutionWhiteImgSrc) {
    // 使用 brightness-0 invert 来尝试让深色文字变白
    // 这个效果可能不完美，因为会反转整个图片的所有颜色
    return `${baseClass} brightness-0 invert`;
  }
  return baseClass;
});

// 处理卡片点击事件
const handleCardClick = (index: number) => {
  const routes = [
    { name: "xr-theater", path: "/xr-theater.html" }, // XR剧院内容及管理方案
    { name: "xr-home", path: "/xr-home.html" }, // XR大空间地产家装方案
    { name: "lbe-custom", path: "/lbe-custom.html" }, // LBE大空间定制开发方案
  ];

  if (routes[index]) {
    router.push(routes[index].path);
  }
};
</script>

<style scoped lang="scss">
/* 滚动动画效果 - 小元素从底部飞入 */
.scroll-fade-in {
  opacity: 0;
  transform: translateY(50px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.scroll-fade-in.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 滚动动画效果 - 从左边飞入（预留，按需使用） */
.scroll-fade-in-left {
  opacity: 0;
  transform: translateX(-50px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.scroll-fade-in-left.fade-in-visible {
  opacity: 1;
  transform: translateX(0);
}

/* 滚动动画效果 - 从右边飞入（预留，按需使用） */
.scroll-fade-in-right {
  opacity: 0;
  transform: translateX(50px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.scroll-fade-in-right.fade-in-visible {
  opacity: 1;
  transform: translateX(0);
}

.title-family {
  font-family: "Noto Sans SC";
  font-weight: normal;
}
</style>
